import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { api, useApi } from "../lib/api";
import { Badge, Button, Card, Empty, ErrorNote, fmtDate, Loading, Menu, Modal, Notice, PageHeader, StickyBar, Table, Td, useAction } from "../components/ui";
import { buildSteps, SequenceTimeline, SignatureNote, type Step } from "../components/Sequence";

const DRAFT_LABEL: Record<string, string> = { queued: "drafting", drafting: "drafting", ready: "ready to review", approved: "approved", failed: "drafting failed" };

export default function CampaignDetail() {
  const { id } = useParams();
  const { data, error, loading, reload } = useApi<any>(`/campaigns/${id}`, [id]);
  const [tab, setTab] = useState<"sequence" | "contacts" | "signals">("sequence");
  const [ask, setAsk] = useState<{ step: Step; value: { subject?: string; body: string }; secs: number; count: number } | null>(null);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const act = useAction();
  if (loading && !data) return <Loading rows={10} />;
  if (!data) return <ErrorNote error={error} />;
  const c = data.campaign;
  const d = c.draft;
  const drafting = c.draftStatus === "queued" || c.draftStatus === "drafting";
  const findings = [...(data.leak?.blocks ?? []), ...(data.leak?.warnings ?? [])];
  const steps = d ? buildSteps(data.template, d, findings, { editable: true }) : [];
  const live = data.enrollments.filter((e: any) => ["Enrolled", "Active", "Paused"].includes(e.status));
  const signals = live.filter((e: any) => e.signalLine);

  const saveMaster = async (step: Step, value: { subject?: string; body: string }, secs: number, applyToCustomized?: boolean) => {
    const r = await api(`/campaigns/${id}/draft`, { method: "PUT", body: { piece: step.key, ...value, editSeconds: secs, applyToCustomized } });
    if (r.needsDecision) {
      setAsk({ step, value, secs, count: r.customized });
      return;
    }
    await reload();
  };
  const setStatus = (status: string) => act.run(async () => { await api(`/campaigns/${id}/status`, { body: { status } }); reload(); });

  return (
    <div className="space-y-5">
      <PageHeader
        title={c.name}
        sub={
          <span className="flex flex-wrap items-center gap-2">
            {c.offering} · {c.ownerName} · created {fmtDate(c.createdAt)} <Badge tone={c.draftStatus}>{DRAFT_LABEL[c.draftStatus] ?? c.draftStatus}</Badge>
            {c.status === "paused" && <Badge tone="paused">campaign paused</Badge>}
          </span>
        }
        actions={
          <Menu
            items={[
              { label: "Redraft with Claude", onClick: () => act.run(async () => { await api(`/campaigns/${id}/redraft`, { body: {} }); reload(); }), disabled: c.draftStatus === "approved" || drafting },
              c.status === "paused" ? { label: "Resume campaign", onClick: () => setStatus("active") } : { label: "Pause campaign", onClick: () => setStatus("paused") },
            ]}
          />
        }
      />
      <ErrorNote error={act.error} />
      {drafting && (
        <Notice tone="accent">
          <span className="flex items-center gap-2">
            Claude is drafting the sequence for {data.counts.total} contacts…
            <Button tone="ghost" onClick={reload}>
              <RefreshCw size={14} /> Refresh
            </Button>
          </span>
        </Notice>
      )}
      {c.draftStatus === "failed" && <ErrorNote error={`Drafting failed: ${c.draftError}. Use More → Redraft with Claude.`} />}
      {d?.source === "template" && <Notice tone="warn">This is a generic template because Claude wasn't available ({d.fallbackReason}). It can't be approved. Use More → Redraft with Claude.</Notice>}
      {data.approvalBlocker && !drafting && c.draftStatus !== "approved" && d?.source !== "template" && <Notice tone="warn">Approve isn't available yet: {data.approvalBlocker}</Notice>}

      <div className="grid gap-3 sm:grid-cols-4">
        <Counter label="Contacts" value={data.counts.total} />
        <Counter label="Approved" value={data.counts.approved} />
        <Counter label="Need a look" value={data.counts.exceptions} sub={`${data.counts.held} held · ${data.counts.customized} customized · ${data.counts.signalLines} signal lines`} />
        <Counter label="Version" value={d ? `v${d.version}` : "—"} sub={d?.editedBy ? `edited by ${d.editedBy}` : d?.model ? `drafted by ${d.model}` : undefined} />
      </div>

      <div className="flex gap-1 border-b border-line">
        {(["sequence", "contacts", "signals"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === t ? "border-accent font-medium text-ink" : "border-transparent text-muted hover:text-ink"}`}>
            {t === "sequence" ? "Sequence" : t === "contacts" ? `Contacts (${data.counts.total})` : `Signal lines (${signals.length})`}
          </button>
        ))}
      </div>

      {tab === "sequence" && d && (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
          <Card title="Master sequence" actions={<span className="text-xs text-muted">{d.rationale}</span>}>
            <SequenceTimeline steps={steps} threadSubject={d.emails[0].subject} onSave={(s, v, secs) => saveMaster(s, v, secs)} footer={<SignatureNote signer={data.signer} postal={data.postalAddress} />} />
          </Card>
          <Preview campaignId={id!} total={live.length} />
        </div>
      )}

      {tab === "contacts" && (
        <Card pad={false}>
          <div className="p-5">
            <Table head={["Contact", "Account", "Territory", "Needs", "Status"]}>
              {data.enrollments.map((e: any) => (
                <tr key={e.contactId}>
                  <Td>
                    <Link className="font-medium text-accent-text hover:underline" to={`/campaigns/${id}/contacts/${e.contactId}`}>{e.contactName}</Link>
                    <div className="text-xs text-muted">{e.title}</div>
                  </Td>
                  <Td>{e.accountName}</Td>
                  <Td className="text-xs">{e.territoryOwner}</Td>
                  <Td className="text-xs">{e.review ? <span className={e.review.kind === "held" ? "text-warn" : "text-ink-2"}>{e.review.text}</span> : <span className="text-muted">—</span>}</Td>
                  <Td>
                    <Badge tone={e.status === "Enrolled" ? (e.draftStatus === "approved" ? "approved" : "pending") : e.status}>{e.status === "Enrolled" ? (e.draftStatus === "approved" ? "Approved" : "Waiting") : e.status}</Badge>
                    {e.pausedReason && <div className="mt-0.5 text-xs text-muted">{e.pausedReason}</div>}
                  </Td>
                </tr>
              ))}
            </Table>
          </div>
        </Card>
      )}

      {tab === "signals" && (
        <Card title="Signal lines" actions={<span className="text-xs text-muted">One sentence per contact, from their account's public signal. Edit or clear one from the contact.</span>}>
          {!signals.length ? (
            <Empty>No contacts in this campaign have a fresh signal. Email 1 reads fine without the line.</Empty>
          ) : (
            <Table head={["Contact", "Account", "Signal line"]}>
              {signals.map((e: any) => (
                <tr key={e.contactId}>
                  <Td><Link className="text-accent-text hover:underline" to={`/campaigns/${id}/contacts/${e.contactId}`}>{e.contactName}</Link></Td>
                  <Td>{e.accountName}</Td>
                  <Td className="mail max-w-xl">{e.signalLine.replace("{company}", e.accountName ?? "")}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {d && (
        <StickyBar>
          {data.counts.exceptions > 0 && c.draftStatus === "approved" && (
            <Link to={`/campaigns/${id}/review`}>
              <Button>Review {data.counts.exceptions} contact(s)</Button>
            </Link>
          )}
          {c.draftStatus === "approved" ? (
            <span className="text-sm text-good">Approved {fmtDate(c.approvedAt)} by {c.approvedBy}. Edits apply to every unsent email.</span>
          ) : (
            <Button tone="primary" disabled={!!data.approvalBlocker || act.busy} title={data.approvalBlocker ?? ""} onClick={() => setConfirmApprove(true)}>
              Approve campaign
            </Button>
          )}
        </StickyBar>
      )}

      {confirmApprove && (
        <Modal
          title="Approve this campaign?"
          onClose={() => setConfirmApprove(false)}
          actions={
            <>
              <Button onClick={() => setConfirmApprove(false)}>Cancel</Button>
              <Button tone="primary" disabled={act.busy} onClick={() => act.run(async () => { const r = await api(`/campaigns/${id}/approve`, { body: {} }); setConfirmApprove(false); await reload(); if (r.exceptions) location.assign(`/campaigns/${id}/review`); })}>
                Approve
              </Button>
            </>
          }
        >
          <p>
            One approval covers the campaign: {data.counts.pending - data.counts.exceptions} contact(s) are scheduled right away. {data.counts.exceptions > 0 && `${data.counts.exceptions} contact(s) with a held value, a customized email or a signal line go to a short review queue next.`}
          </p>
          <p className="text-muted">Emails go out on the business-day schedule in each contact's time zone. While the kill switch is on, only test-mailbox and dry-run sends happen.</p>
        </Modal>
      )}
      {ask && (
        <Modal
          title="Apply to customized contacts too?"
          onClose={() => setAsk(null)}
          actions={
            <>
              <Button onClick={() => act.run(async () => { await saveMaster(ask.step, ask.value, ask.secs, false); setAsk(null); })}>Keep their versions</Button>
              <Button tone="primary" onClick={() => act.run(async () => { await saveMaster(ask.step, ask.value, ask.secs, true); setAsk(null); })}>Apply to all</Button>
            </>
          }
        >
          {ask.count} contact(s) have their own version of {ask.step.label}. Apply this edit to them too (their customization of this step is replaced), or keep their versions?
        </Modal>
      )}
    </div>
  );
}

function Counter({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface px-4 py-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-xl font-semibold tabular-nums text-ink">{value}</div>
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </div>
  );
}

/** The master rendered for 3 real contacts, with "show next" (Phase 3.4). */
function Preview({ campaignId, total }: { campaignId: string; total: number }) {
  const [offset, setOffset] = useState(0);
  const [i, setI] = useState(0);
  const { data, loading } = useApi<any>(`/campaigns/${campaignId}/preview?offset=${offset}&size=3`, [offset]);
  const item = data?.items?.[i];
  const next = () => (i < (data?.items?.length ?? 0) - 1 ? setI(i + 1) : offset + 3 < total ? (setOffset(offset + 3), setI(0)) : null);
  const prev = () => (i > 0 ? setI(i - 1) : offset > 0 ? (setOffset(Math.max(0, offset - 3)), setI(2)) : null);
  return (
    <Card
      title="Preview for real contacts"
      actions={
        <span className="flex items-center gap-1 text-xs text-muted">
          {total ? offset + i + 1 : 0} of {total}
          <Button tone="ghost" onClick={prev} aria-label="Previous contact">
            <ChevronLeft size={15} />
          </Button>
          <Button tone="ghost" onClick={next} aria-label="Show next contact">
            Show next <ChevronRight size={15} />
          </Button>
        </span>
      }
    >
      {loading && !data ? (
        <Loading rows={5} />
      ) : !item ? (
        <Empty>No contacts to preview.</Empty>
      ) : (
        <div className="space-y-3">
          <div>
            <Link className="text-sm font-medium text-accent-text hover:underline" to={`/campaigns/${campaignId}/contacts/${item.contactId}`}>{item.contactName}</Link>
            <div className="text-xs text-muted">{item.title} · {item.accountName}</div>
            {item.hold && <div className="mt-1 text-xs text-warn">Held back: {item.hold}</div>}
            {item.customized && <div className="mt-1 text-xs text-accent-text">Customized</div>}
          </div>
          {item.rendered && (
            <div className="rounded-lg border border-line bg-surface-2 p-3">
              <div className="mb-2 text-sm"><span className="text-muted">Subject </span><span className="font-medium">{item.rendered.emails[0].subject}</span></div>
              <div className="mail text-sm">{item.rendered.emails[0].body}</div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
